import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import axios from 'axios';

const API =
  process.env.REACT_APP_API_URL ||
  'https://books.bloanshandbags.com/api';

const BRAND = {
  olive:'#556B2F',
  cream:'#F2EFE7',
  gold:'#A8893D',
  brown:'#3B2A21',
  red:'#8F2E24'
};

const emptyForm = {
  bank_name:'',
  bank_code:'',
  bank_account_name:'',
  bank_account_number:'',
  bank_account_type:'Savings',
  account_name_diff_explanation:'',
  confirmed:false,
  certified:false
};

const normalizeName = value => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');
};

export default function PayrollForm({ token }) {
  const [step, setStep] = useState('loading');
  const [steward, setSteward] = useState(null);
  const [banks, setBanks] = useState([]);
  const [bankSearch, setBankSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] =
    useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [verificationError, setVerificationError] =
    useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifiedDetails, setVerifiedDetails] =
    useState(null);
  const [saving, setSaving] = useState(false);

  const verificationSequence = useRef(0);
  const bankRef = useRef(null);

  const inp = {
    width:'100%',
    padding:'10px 14px',
    border:'1px solid #E8E0D4',
    borderRadius:8,
    fontSize:13,
    color:BRAND.brown,
    background:'#fff',
    boxSizing:'border-box',
    fontFamily:'Georgia,serif'
  };

  const lbl = {
    display:'block',
    fontSize:11,
    fontWeight:700,
    color:'#8A8A8A',
    textTransform:'uppercase',
    letterSpacing:'.07em',
    marginBottom:5
  };

  const updateForm = (key, value) => {
    setForm(current => ({
      ...current,
      [key]:value
    }));
  };

  const clearVerification = () => {
    verificationSequence.current += 1;
    setVerifiedDetails(null);
    setVerificationError('');
    setVerifying(false);

    setForm(current => ({
      ...current,
      bank_account_name:'',
      confirmed:false,
      certified:false
    }));
  };

  useEffect(() => {
    const validToken =
      typeof token === 'string' &&
      /^[a-f0-9]{64}$/i.test(token);

    if (!validToken) {
      setError(
        'This payroll enrolment link is invalid. Please request a new link from HR.'
      );
      setStep('invalid');
      return;
    }

    let active = true;

    const load = async () => {
      try {
        const [formResponse, bankResponse] =
          await Promise.all([
            axios.get(
              `${API}/biodata/payroll/form/${token}`
            ),
            axios.get(
              `${API}/biodata/payroll/banks/${token}`
            )
          ]);

        if (!active) return;

        const record = formResponse.data?.data;
        const returnedBanks =
          bankResponse.data?.data || [];

        if (!record || !record.full_name) {
          throw new Error('Invalid payroll record');
        }

        setSteward(record);
        setBanks(
          [...returnedBanks].sort((a, b) =>
            String(a.name).localeCompare(
              String(b.name)
            )
          )
        );
        setStep('form');
      } catch (requestError) {
        if (!active) return;

        setError(
          requestError.response?.data?.error ||
          'This payroll enrolment link is invalid, expired or already used.'
        );
        setStep('invalid');
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    const closeDropdown = event => {
      if (
        bankRef.current &&
        !bankRef.current.contains(event.target)
      ) {
        setShowBankDropdown(false);
      }
    };

    document.addEventListener(
      'mousedown',
      closeDropdown
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        closeDropdown
      );
    };
  }, []);

  useEffect(() => {
    const accountNumber =
      form.bank_account_number;
    const bankCode = form.bank_code;

    const sequence =
      verificationSequence.current + 1;

    verificationSequence.current = sequence;

    setVerifiedDetails(null);
    setVerificationError('');
    setForm(current => ({
      ...current,
      bank_account_name:'',
      confirmed:false,
      certified:false
    }));

    if (
      !/^\d{10}$/.test(accountNumber) ||
      !bankCode ||
      !token
    ) {
      setVerifying(false);
      return;
    }

    const timer = setTimeout(async () => {
      setVerifying(true);

      try {
        const response = await axios.post(
          `${API}/biodata/payroll/verify-account/${token}`,
          {
            account_number:accountNumber,
            bank_code:bankCode,
            bank_name:form.bank_name
          }
        );

        if (
          verificationSequence.current !== sequence
        ) {
          return;
        }

        const accountName =
          response.data?.account_name || '';
        const verifiedBankName =
          response.data?.bank_name || '';
        const verifiedBankCode =
          response.data?.bank_code || '';

        if (
          !accountName ||
          verifiedBankCode !== bankCode
        ) {
          throw new Error(
            'Incomplete verification response'
          );
        }

        const details = {
          accountNumber,
          bankCode,
          accountName,
          bankName:verifiedBankName
        };

        setVerifiedDetails(details);

        setForm(current => ({
          ...current,
          bank_name:verifiedBankName,
          bank_code:verifiedBankCode,
          bank_account_name:accountName
        }));
      } catch (requestError) {
        if (
          verificationSequence.current !== sequence
        ) {
          return;
        }

        setVerificationError(
          requestError.response?.data?.error ||
          'We could not verify this account. Check the bank and account number.'
        );
      } finally {
        if (
          verificationSequence.current === sequence
        ) {
          setVerifying(false);
        }
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [
    form.bank_account_number,
    form.bank_code,
    token
  ]);

  const selectBank = bank => {
    clearVerification();

    setForm(current => ({
      ...current,
      bank_name:bank.name,
      bank_code:String(bank.code),
      bank_account_name:''
    }));

    setBankSearch(bank.name);
    setShowBankDropdown(false);
    setError('');
  };

  const changeBankSearch = value => {
    clearVerification();

    setBankSearch(value);
    setShowBankDropdown(true);

    setForm(current => ({
      ...current,
      bank_name:'',
      bank_code:'',
      bank_account_name:''
    }));
  };

  const changeAccountNumber = value => {
    const digits = value
      .replace(/\D/g, '')
      .slice(0, 10);

    clearVerification();

    setForm(current => ({
      ...current,
      bank_account_number:digits,
      bank_account_name:''
    }));

    setError('');
  };

  const nameMismatch =
    Boolean(verifiedDetails) &&
    normalizeName(verifiedDetails.accountName) !==
    normalizeName(steward?.full_name);

  const verificationIsCurrent =
    Boolean(verifiedDetails) &&
    verifiedDetails.accountNumber ===
      form.bank_account_number &&
    verifiedDetails.bankCode === form.bank_code &&
    verifiedDetails.accountName ===
      form.bank_account_name;

  const filteredBanks = banks.filter(bank =>
    String(bank.name)
      .toLowerCase()
      .includes(bankSearch.toLowerCase())
  );

  const submit = async () => {
    setError('');

    if (!form.bank_code || !form.bank_name) {
      setError('Please select your bank from the list.');
      return;
    }

    if (
      !/^\d{10}$/.test(
        form.bank_account_number
      )
    ) {
      setError(
        'Account number must be exactly 10 digits.'
      );
      return;
    }

    if (!verificationIsCurrent) {
      setError(
        'Your account must be successfully verified before submission.'
      );
      return;
    }

    if (
      nameMismatch &&
      form.account_name_diff_explanation
        .trim().length < 5
    ) {
      setError(
        'Please explain why the verified account name differs from your steward name.'
      );
      return;
    }

    if (!form.confirmed) {
      setError(
        'Please confirm that you checked the account number.'
      );
      return;
    }

    if (!form.certified) {
      setError(
        'Please certify that the information is accurate.'
      );
      return;
    }

    setSaving(true);

    try {
      const response = await axios.post(
        `${API}/biodata/payroll/submit/${token}`,
        {
          account_number:
            form.bank_account_number,
          bank_code:form.bank_code,
          bank_name:form.bank_name,
          account_type:
            form.bank_account_type,
          name_difference_explanation:
            form.account_name_diff_explanation
              .trim()
        }
      );

      setForm(current => ({
        ...current,
        bank_account_name:
          response.data?.account_name ||
          current.bank_account_name
      }));

      setStep('submitted');
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        'Submission failed. Please try again.'
      );

      if (
        requestError.response?.data?.code ===
          'ACCOUNT_VERIFICATION_FAILED' ||
        requestError.response?.data?.code ===
          'PAYROLL_LINK_ALREADY_USED'
      ) {
        clearVerification();
      }
    } finally {
      setSaving(false);
    }
  };

  if (step === 'loading') {
    return (
      <div style={{
        maxWidth:520,
        margin:'48px auto',
        padding:24,
        textAlign:'center',
        fontFamily:'Georgia,serif'
      }}>
        <div className="spinner" />
        <p style={{color:'#666'}}>
          Loading secure payroll enrolment...
        </p>
      </div>
    );
  }

  if (step === 'invalid') {
    return (
      <div style={{
        maxWidth:520,
        margin:'48px auto',
        padding:24,
        textAlign:'center',
        fontFamily:'Georgia,serif'
      }}>
        <div style={{
          background:'#fff',
          borderRadius:16,
          padding:36,
          boxShadow:'0 4px 24px rgba(0,0,0,.08)'
        }}>
          <div style={{
            fontSize:42,
            marginBottom:14
          }}>
            🔒
          </div>

          <h2 style={{
            color:BRAND.red,
            marginBottom:12
          }}>
            Link unavailable
          </h2>

          <p style={{
            color:'#555',
            fontSize:14,
            lineHeight:1.7
          }}>
            {error}
          </p>

          <p style={{
            color:'#8A8A8A',
            fontSize:12,
            marginTop:16
          }}>
            Please contact HR for a new payroll
            enrolment link.
          </p>
        </div>
      </div>
    );
  }

  if (step === 'submitted') {
    return (
      <div style={{
        maxWidth:520,
        margin:'48px auto',
        padding:24,
        textAlign:'center',
        fontFamily:'Georgia,serif'
      }}>
        <div style={{
          background:'#fff',
          borderRadius:16,
          padding:40,
          boxShadow:'0 4px 24px rgba(0,0,0,.08)'
        }}>
          <div style={{
            fontSize:52,
            marginBottom:16
          }}>
            ✅
          </div>

          <h2 style={{
            color:BRAND.olive,
            marginBottom:12
          }}>
            Submitted Successfully
          </h2>

          <p style={{
            color:'#555',
            fontSize:14,
            lineHeight:1.7
          }}>
            Thank you,{' '}
            <strong>{steward?.full_name}</strong>.
            Your verified bank details have been
            submitted for HR review.
          </p>

          <p style={{
            color:'#8A8A8A',
            fontSize:12,
            marginTop:16
          }}>
            This secure link has now been used and
            cannot be submitted again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth:640,
      margin:'0 auto',
      padding:'24px 16px',
      fontFamily:'Georgia,serif'
    }}>
      <div style={{
        background:BRAND.brown,
        borderRadius:10,
        padding:'14px 18px',
        marginBottom:20
      }}>
        <div style={{
          color:BRAND.gold,
          fontSize:14,
          fontWeight:700
        }}>
          Payroll Enrolment Form
        </div>

        <div style={{
          color:'rgba(242,239,231,.75)',
          fontSize:12,
          marginTop:3
        }}>
          Bloans Creations Ltd. ·{' '}
          <strong>{steward?.full_name}</strong>
        </div>
      </div>

      <div style={{
        background:'#fff',
        borderRadius:12,
        padding:28,
        boxShadow:'0 2px 14px rgba(0,0,0,.06)'
      }}>
        <div style={{
          background:'#FDEAEA',
          border:'1px solid #F5B8B8',
          borderRadius:8,
          padding:14,
          marginBottom:20,
          fontSize:12,
          color:BRAND.red
        }}>
          <strong>Security Notice:</strong>{' '}
          Bloans will never ask for your bank
          password, card PIN, OTP or internet
          banking credentials.
        </div>

        <p style={{
          fontSize:13,
          color:'#555',
          lineHeight:1.8,
          marginBottom:20
        }}>
          Enter the bank account where your salary
          should be paid. The account name will be
          retrieved securely from the bank.
        </p>

        <div
          ref={bankRef}
          style={{
            marginBottom:14,
            position:'relative'
          }}
        >
          <label style={lbl}>Bank Name *</label>

          <input
            style={inp}
            value={bankSearch}
            onChange={event =>
              changeBankSearch(event.target.value)
            }
            onFocus={() =>
              setShowBankDropdown(true)
            }
            placeholder="Type to search banks"
            autoComplete="off"
          />

          {showBankDropdown && (
            <div style={{
              position:'absolute',
              zIndex:100,
              width:'100%',
              background:'#fff',
              border:'1px solid #E8E0D4',
              borderRadius:8,
              boxShadow:'0 4px 16px rgba(0,0,0,.1)',
              maxHeight:220,
              overflowY:'auto',
              top:'calc(100% + 4px)'
            }}>
              {filteredBanks.length === 0 ? (
                <div style={{
                  padding:12,
                  color:'#8A8A8A',
                  fontSize:13
                }}>
                  No banks found
                </div>
              ) : (
                filteredBanks.map(bank => (
                  <button
                    type="button"
                    key={`${bank.code}-${bank.name}`}
                    onClick={() => selectBank(bank)}
                    style={{
                      display:'block',
                      width:'100%',
                      padding:'10px 14px',
                      cursor:'pointer',
                      fontSize:13,
                      color:BRAND.brown,
                      border:'none',
                      borderBottom:
                        '1px solid #F2EFE7',
                      background:'#fff',
                      textAlign:'left'
                    }}
                  >
                    {bank.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>
            Account Number * (10 digits)
          </label>

          <input
            style={inp}
            value={form.bank_account_number}
            maxLength={10}
            inputMode="numeric"
            autoComplete="off"
            onChange={event =>
              changeAccountNumber(
                event.target.value
              )
            }
            placeholder="10-digit NUBAN account number"
          />

          {verifying && (
            <div style={{
              fontSize:11,
              color:BRAND.olive,
              marginTop:5
            }}>
              Verifying account...
            </div>
          )}

          {verificationError && !verifying && (
            <div style={{
              fontSize:12,
              color:BRAND.red,
              background:'#FDEAEA',
              borderRadius:6,
              padding:'7px 10px',
              marginTop:6
            }}>
              {verificationError}
            </div>
          )}

          {verificationIsCurrent && !verifying && (
            <div style={{
              fontSize:12,
              color:'#2E6B3A',
              fontWeight:700,
              background:'#E8F5EB',
              borderRadius:6,
              padding:'7px 10px',
              marginTop:6
            }}>
              ✅ Bank account verified
            </div>
          )}
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>
            Verified Account Name *
          </label>

          <input
            style={{
              ...inp,
              background:'#F3F1EC',
              cursor:'not-allowed',
              fontWeight:700
            }}
            value={form.bank_account_name}
            readOnly
            tabIndex={-1}
            placeholder="Retrieved automatically"
          />

          <div style={{
            fontSize:11,
            color:'#8A8A8A',
            marginTop:4
          }}>
            This field is supplied by the bank and
            cannot be edited.
          </div>
        </div>

        {nameMismatch && (
          <div style={{
            background:'#FFF3DC',
            border:'1px solid #F0D9A0',
            borderRadius:8,
            padding:'10px 14px',
            marginBottom:14,
            fontSize:12,
            color:'#8B5E0A'
          }}>
            The verified account name{' '}
            <strong>
              "{form.bank_account_name}"
            </strong>{' '}
            differs from the steward name{' '}
            <strong>
              "{steward?.full_name}"
            </strong>.
          </div>
        )}

        <div style={{marginBottom:14}}>
          <label style={lbl}>Account Type</label>

          <select
            style={inp}
            value={form.bank_account_type}
            onChange={event =>
              updateForm(
                'bank_account_type',
                event.target.value
              )
            }
          >
            <option value="Savings">Savings</option>
            <option value="Current">Current</option>
          </select>
        </div>

        {nameMismatch && (
          <div style={{marginBottom:14}}>
            <label style={lbl}>
              Explanation for Name Difference *
            </label>

            <textarea
              style={{
                ...inp,
                minHeight:75
              }}
              value={
                form.account_name_diff_explanation
              }
              maxLength={1000}
              onChange={event =>
                updateForm(
                  'account_name_diff_explanation',
                  event.target.value
                )
              }
              placeholder="Explain the difference, for example a maiden name."
            />
          </div>
        )}

        <div style={{
          background:'#F9F8F5',
          border:'1px solid #E8E0D4',
          borderRadius:8,
          padding:16,
          marginTop:20,
          marginBottom:16
        }}>
          <div style={{
            fontWeight:700,
            fontSize:12,
            color:BRAND.brown,
            marginBottom:10,
            textTransform:'uppercase',
            letterSpacing:'.06em'
          }}>
            Steward Confirmation
          </div>

          <ol style={{
            fontSize:13,
            paddingLeft:18,
            margin:0,
            lineHeight:2,
            color:BRAND.brown
          }}>
            <li>
              The verified bank account belongs to me.
            </li>
            <li>
              I authorise Bloans Creations Ltd. to
              pay my salary into this account.
            </li>
            <li>
              I will notify HR if my bank details
              change.
            </li>
            <li>
              The information I supplied is accurate.
            </li>
          </ol>
        </div>

        <label style={{
          display:'flex',
          alignItems:'flex-start',
          gap:10,
          cursor:verificationIsCurrent
            ? 'pointer'
            : 'not-allowed',
          marginBottom:12
        }}>
          <input
            type="checkbox"
            style={{
              accentColor:BRAND.olive,
              width:18,
              height:18,
              marginTop:2
            }}
            disabled={!verificationIsCurrent}
            checked={form.confirmed}
            onChange={event =>
              updateForm(
                'confirmed',
                event.target.checked
              )
            }
          />

          <span style={{
            fontSize:13,
            color:BRAND.brown,
            lineHeight:1.7
          }}>
            I have checked account number{' '}
            <strong>
              {form.bank_account_number || '—'}
            </strong>{' '}
            and confirm it is correct.
          </span>
        </label>

        <label style={{
          display:'flex',
          alignItems:'flex-start',
          gap:10,
          cursor:verificationIsCurrent
            ? 'pointer'
            : 'not-allowed',
          marginBottom:20
        }}>
          <input
            type="checkbox"
            style={{
              accentColor:BRAND.olive,
              width:18,
              height:18,
              marginTop:2
            }}
            disabled={!verificationIsCurrent}
            checked={form.certified}
            onChange={event =>
              updateForm(
                'certified',
                event.target.checked
              )
            }
          />

          <span style={{
            fontSize:13,
            color:BRAND.brown,
            lineHeight:1.7
          }}>
            I,{' '}
            <strong>{steward?.full_name}</strong>,
            certify that this information is accurate.
          </span>
        </label>

        {error && (
          <div style={{
            background:'#FDEAEA',
            border:'1px solid #F5B8B8',
            borderRadius:8,
            padding:'10px 14px',
            marginBottom:16,
            fontSize:13,
            color:BRAND.red
          }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={
            saving ||
            verifying ||
            !verificationIsCurrent
          }
          style={{
            width:'100%',
            padding:13,
            borderRadius:10,
            border:'none',
            background:
              saving ||
              verifying ||
              !verificationIsCurrent
                ? '#B8B8B8'
                : BRAND.olive,
            color:'#fff',
            cursor:
              saving ||
              verifying ||
              !verificationIsCurrent
                ? 'not-allowed'
                : 'pointer',
            fontSize:15,
            fontWeight:700
          }}
        >
          {saving
            ? 'Submitting...'
            : verifying
              ? 'Verifying account...'
              : 'Submit Payroll Enrolment'}
        </button>
      </div>
    </div>
  );
}
